import { Action, createReducer, on } from '@ngrx/store';
import { LobbyServiceWithCategory } from '../../shared/models/lobby-service';
import { StaffMember } from '../../shared/models/staff-member';
import * as LobbyCheckInPageActions from './actions/lobby-check-in-page.actions';
import * as MessageHubActions from '../../../state/actions/message-hub.actions';
import * as LobbyCheckInAPIActions from './actions/lobby-api.actions';

export interface LobbyCheckInState {
    tenantId: number | null;
    locationId: number | null;
    locationTimezone: string;
    isLocationChanged: boolean;
    lobbyId: number | null;
    currentLobbyEntryId: number;
    staffMembers: StaffMember[];
    lobbyServices: LobbyServiceWithCategory[];
    selectedLobbyServices: number[];
    serviceFilter: string;
    spanishSpeakerPreferred: boolean;
    instantAssistedLobbyEntryId: number | null;
    showLobbyCheckInPopup:boolean;
    showInstantImpersonatePopup:boolean;
}

const initialLobbyCheckInState: LobbyCheckInState = {
    tenantId: null,
    locationId: null,
    locationTimezone: 'Sri Lanka Standard Time',
    isLocationChanged: false,
    lobbyId: null,
    currentLobbyEntryId: 0,
    staffMembers: [],
    lobbyServices: [],
    selectedLobbyServices: [],
    serviceFilter: '',
    spanishSpeakerPreferred: false,
    instantAssistedLobbyEntryId: null,
    showLobbyCheckInPopup:false,
    showInstantImpersonatePopup:false
};

const reducer = createReducer<LobbyCheckInState>(
    initialLobbyCheckInState,
    on(LobbyCheckInPageActions.setLocationId, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            locationId: action.locationId,
            isLocationChanged: action.locationId !== lobbyCheckInState.locationId
        }
    )),
    on(LobbyCheckInPageActions.setTimezoneId, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            locationTimezone: action.timezoneId
        }
    )),
    on(LobbyCheckInPageActions.setLobbyCheckInPopupFlag, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            showLobbyCheckInPopup: action.showCheckInPopup
        }
    )),
    on(LobbyCheckInPageActions.setTenantId, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            tenantId: action.tenantId,
            isLocationChanged: action.tenantId !== lobbyCheckInState.tenantId
        }
    )),
    on(LobbyCheckInPageActions.readyForTheNextCheckIn, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            currentLobbyEntryId: 0
        }
    )),
    on(MessageHubActions.receivedSignedInUsersAndGroupsData, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            staffMembers: (action.messageData !== '') ? JSON.parse(action.messageData) : []
        }
    )),
    on(LobbyCheckInAPIActions.loadLobbyDetailsSuccess, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            lobbyId: (action.lobby.length > 0) ? action.lobby[0].lobbyId : 0,
            isLocationChanged: false
        }
    )),
    on(LobbyCheckInAPIActions.loadServicesSuccess, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            lobbyServices: action.services.map(s => ({
                ...s,
                service: {...s.service, isChecked: false}
            }))
        }
    )),
    on(LobbyCheckInAPIActions.addToLobbySuccess, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            currentLobbyEntryId: -1, // set lobby entry id to -1 for identify lobby checkin is success
            selectedLobbyServices: [],
            serviceFilter: '',
            spanishSpeakerPreferred: false,
            showLobbyCheckInPopup:false
        }
    )),
    on(LobbyCheckInAPIActions.addToLobbyFailure, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,           
            showLobbyCheckInPopup:true
        }
    )),
    on(LobbyCheckInAPIActions.instantAssistSuccess, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            instantAssistedLobbyEntryId: action.lobbyEntryId
        }
    )),
    on(LobbyCheckInAPIActions.impersonateInstantAssistSuccess, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            instantAssistedLobbyEntryId: action.lobbyEntryId,
            
        }
    )),
    on(LobbyCheckInPageActions.serviceCheckboxChecked, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            selectedLobbyServices: lobbyCheckInState.selectedLobbyServices.concat([action.serviceId])
        }
    )),
    on(LobbyCheckInPageActions.serviceCheckboxUnchecked, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            selectedLobbyServices: lobbyCheckInState.selectedLobbyServices.filter(s => s !== action.serviceId)
        }
    )),
    on(LobbyCheckInPageActions.serviceFilterValueChange, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            serviceFilter: action.filterValue
        }
    )),
    on(LobbyCheckInPageActions.spanishSpeakerPreferredChange, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            spanishSpeakerPreferred: action.spanishSpeakerPreferred
        }
    )),
    on(LobbyCheckInPageActions.loadInstantImpersonateAssist, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            showInstantImpersonatePopup: action.show
        }
    )),
    on(LobbyCheckInPageActions.closeCheckInModal, (lobbyCheckInState, action): LobbyCheckInState => (
        {
            ...lobbyCheckInState,
            selectedLobbyServices: [],
            serviceFilter: '',
            spanishSpeakerPreferred: false,
            instantAssistedLobbyEntryId: null,
            showLobbyCheckInPopup:false,
            currentLobbyEntryId: -1 //Set lobby entry id= -1 to reset and rebind form values with default lobby entry
        }
    ))
);

export function lobbyCheckInReducer(state: LobbyCheckInState | undefined, action: Action) {
    return reducer(state, action);
}
